import { FormEvent, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ErrorOwner, ErrorRelationState } from '../../api/api';
import {
    DESCRIPTIVE_ROLES,
    Dossier,
    DossierAction,
    DossierDetail,
    DossierMoment,
    DossierMomentKind,
    DossierNode,
    ROLE_LABEL,
} from '../../api/dossier';
import { DossierMonitor } from '../../hooks/useDossier';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { TraceCallout } from '../Trace/TraceCallout';
import {
    InterpretationNote,
    OwnerBadge,
} from '../Interpretation/InterpretationNote';
import { ObjectFlow } from '../Money/ObjectFlow';
import { SettlementView } from './SettlementView';
import './Dossier.scss';

const STATE: Record<
    ErrorRelationState,
    { label: string; tone: string; hint: string }
> = {
    [ErrorRelationState.InExact]: {
        label: 'In Exact',
        tone: 'ok',
        hint: 'De koppeltabel heeft een Exact-id voor dit object.',
    },
    [ErrorRelationState.NotInExact]: {
        label: 'Niet in Exact',
        tone: 'warn',
        hint: 'Nog niet (helemaal) in Exact: geen Exact-id, of de laatste wijziging kwam niet door.',
    },
    [ErrorRelationState.Skipped]: {
        label: 'Overgeslagen',
        tone: 'muted',
        hint: 'Bewust niet naar Exact gestuurd.',
    },
    [ErrorRelationState.RemovedByChargeback]: {
        label: 'Verwijderd na stornering',
        tone: 'info',
        hint: 'Stond als bankboeking in Exact en is na een stornering door de connector verwijderd.',
    },
    [ErrorRelationState.ChargebackProcessed]: {
        label: 'Verwerkt',
        tone: 'ok',
        hint: 'De bankboeking van de bijbehorende betaling is uit Exact verwijderd.',
    },
    [ErrorRelationState.Unknown]: {
        label: 'Onbekend',
        tone: 'muted',
        hint: 'Er is geen koppelregel voor dit object.',
    },
};

const MOMENT: Record<DossierMomentKind, { icon: IconProp; label: string }> = {
    Failed: { icon: 'circle-xmark', label: 'Mislukt' },
    Succeeded: { icon: 'circle-check', label: 'Gelukt' },
    Requeued: { icon: 'rotate-left', label: 'Opnieuw aangeboden' },
    Removed: { icon: 'ban', label: 'Verwijderd' },
    Note: { icon: 'circle-info', label: 'Melding' },
};

const time = (iso?: string | null): string =>
    iso
        ? new Date(iso).toLocaleTimeString('nl-NL', {
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

const dayOf = (iso?: string | null): string =>
    iso ? new Date(iso).toDateString() : '';

const dayLabel = (iso?: string | null): string =>
    iso
        ? new Date(iso).toLocaleDateString('nl-NL', {
              weekday: 'short',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : 'Zonder tijdstip';

const stamp = (iso?: string | null): string =>
    iso
        ? new Date(iso).toLocaleString('nl-NL', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

/** "12:16" or "12:16–19:30", with the day when the range runs over more than one. */
const when = (moment: DossierMoment): string => {
    if (moment.count <= 1 || !moment.lastAt) {
        return time(moment.firstAt);
    }

    return dayOf(moment.firstAt) === dayOf(moment.lastAt)
        ? `${time(moment.firstAt)}–${time(moment.lastAt)}`
        : `${time(moment.firstAt)}–${stamp(moment.lastAt)}`;
};

const nodeLabel = (node?: DossierNode): string =>
    node ? `${ROLE_LABEL[node.role]} ${node.relationId}` : '';

/**
 * What the skill c2e-research-error needs to explain a dossier: all of it, without the
 * timestamp of every single attempt - the first, the last and the count say enough.
 */
const forSkill = (dossier: Dossier) => ({
    request:
        'Zoek met de skill c2e-research-error uit wat er in dit dossier misgaat, en of uitleg en afhandeling van de issues kloppen met wat het dossier laat zien.',
    format: 'dossier/1',
    exportedAt: new Date().toISOString(),
    ...dossier,
    moments: dossier.moments.map(({ occurrences, ...moment }) => moment),
});

/** A failure, a requeue, a removal or a success: everything but the routine notes. */
const isNoteworthy = (moment: DossierMoment): boolean => moment.kind !== 'Note';

export interface IDossierPanelProps {
    monitor: DossierMonitor;
    open: boolean;
    onToggle: () => void;
    /** Opens the trace of one object of the dossier. */
    onTrace: (id: string, objectType: string, relationId: string) => void;
}

/**
 * Everything around one number, in the order the question is asked: the verdict, what has
 * to happen and by whom, which objects are involved, and what happened to them.
 *
 * Any number will do as a start - an invoice, a payment or chargeback transaction, a credit
 * note, a customer - and the server works out what it is.
 */
export const DossierPanel = ({
    monitor,
    open,
    onToggle,
    onTrace,
}: IDossierPanelProps) => {
    const [query, setQuery] = useState<string>('');
    // Live by default: without them a dossier only knows what the logs knew at the time.
    const [askChargebee, setAskChargebee] = useState<boolean>(true);
    const [askExact, setAskExact] = useState<boolean>(true);
    const [focus, setFocus] = useState<string | null>(null);
    const [onlyNoteworthy, setOnlyNoteworthy] = useState<boolean>(true);
    const [copied, setCopied] = useState<boolean>(false);

    const dossier = monitor.dossier;

    // A dossier opened from elsewhere - a row in the error log, a trace - fills the field.
    useEffect(() => {
        if (monitor.request?.id) {
            setQuery(monitor.request.id);
            setAskChargebee(!!monitor.request.chargebee);
            setAskExact(!!monitor.request.exact);
        }

        setFocus(null);
    }, [monitor.request]);

    const nodesByKey = useMemo(
        () => new Map((dossier?.nodes ?? []).map((node) => [node.key, node])),
        [dossier]
    );

    const moments = useMemo(
        () =>
            (dossier?.moments ?? []).filter(
                (moment) =>
                    (!focus || moment.nodeKey === focus) &&
                    (!onlyNoteworthy || isNoteworthy(moment))
            ),
        [dossier, focus, onlyNoteworthy]
    );

    const days = useMemo(() => {
        const grouped: {
            day: string;
            label: string;
            moments: DossierMoment[];
        }[] = [];

        moments.forEach((moment) => {
            const key = dayOf(moment.firstAt);
            const last = grouped[grouped.length - 1];

            if (last && last.day === key) {
                last.moments.push(moment);
            } else {
                grouped.push({
                    day: key,
                    label: dayLabel(moment.firstAt),
                    moments: [moment],
                });
            }
        });

        return grouped;
    }, [moments]);

    const copy = () => {
        if (!dossier) {
            return;
        }

        navigator.clipboard
            ?.writeText(JSON.stringify(forSkill(dossier), null, 2))
            .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => setCopied(false));
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        monitor.open({ id: query, chargebee: askChargebee, exact: askExact });
    };

    const openActions = (dossier?.actions ?? []).filter(
        (a) => a.owner !== ErrorOwner.None
    );
    const doneActions = (dossier?.actions ?? []).filter(
        (a) => a.owner === ErrorOwner.None
    );

    const rootLabel = dossier
        ? dossier.kind === 'Customer'
            ? `Klant ${dossier.rootId}`
            : dossier.kind === 'Invoice'
            ? `Factuur ${dossier.rootId}`
            : dossier.rootId
        : undefined;

    return (
        <CollapsibleSection
            id="dossier"
            title="Dossier"
            hint="Factuur, betalingen, storneringen en klant bij elkaar, met één tijdlijn"
            open={open}
            onToggle={onToggle}
            busy={monitor.loading}
            status={rootLabel}
            tools={
                <form className="dossier-search" onSubmit={submit}>
                    <input
                        type="text"
                        className="form-control dossier-search__input"
                        placeholder="Factuur-, betaal-, storno- of klantnummer"
                        aria-label="Nummer om op te zoeken"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />

                    <label
                        className="dossier-search__check"
                        title="Vraagt Chargebee welke betalingen er op de factuur staan. Betalingen die in één keer slaagden staan namelijk nergens in de logboeken."
                    >
                        <input
                            type="checkbox"
                            checked={askChargebee}
                            onChange={(e) => setAskChargebee(e.target.checked)}
                        />
                        Ook Chargebee raadplegen
                    </label>

                    <label
                        className="dossier-search__check"
                        title="Vraagt Exact hoe de factuur daar is afgeletterd: de boeking, elke post (ook de afgeletterde) en wat er nog openstaat. Telt mee voor de daglimiet van Exact."
                    >
                        <input
                            type="checkbox"
                            checked={askExact}
                            onChange={(e) => setAskExact(e.target.checked)}
                        />
                        Ook Exact raadplegen
                    </label>

                    <button
                        type="submit"
                        className="btn btn-sm btn-primary"
                        disabled={!query.trim() || monitor.loading}
                    >
                        Dossier openen
                    </button>
                </form>
            }
        >
            {monitor.error && (
                <TraceCallout tone="danger" title="Ophalen mislukt">
                    {monitor.error}
                </TraceCallout>
            )}

            {monitor.loading && (
                <DossierSkeleton
                    query={monitor.request?.id}
                    onCancel={monitor.cancel}
                />
            )}

            {!dossier && !monitor.loading && !monitor.error && (
                <p className="dossier__muted">
                    Vul hierboven een nummer in, of kies "Dossier" bij een regel
                    in het foutenlogboek of in een trace.
                </p>
            )}

            {dossier && !monitor.loading && (
                <div className="dossier">
                    <header
                        className={`dossier__verdict dossier__verdict--${
                            openActions.length > 0 ? 'open' : 'done'
                        }`}
                    >
                        <FontAwesomeIcon
                            icon={
                                openActions.length > 0
                                    ? 'triangle-exclamation'
                                    : 'circle-check'
                            }
                            className="dossier__verdict-icon"
                        />
                        <div className="dossier__verdict-text">
                            <p className="dossier__conclusion">
                                {dossier.conclusion}
                            </p>
                            {dossier.resolvedAs && (
                                <p className="dossier__muted">
                                    {dossier.resolvedAs}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary dossier__copy"
                            onClick={copy}
                            title="Dit dossier als JSON, om in Claude Code aan de skill c2e-research-error te geven"
                        >
                            <FontAwesomeIcon icon="copy" />{' '}
                            {copied ? 'Gekopieerd' : 'Kopieer voor de skill'}
                        </button>
                    </header>

                    {openActions.length > 0 && (
                        <section
                            className="dossier__block"
                            aria-label="Wat er moet gebeuren"
                        >
                            <h4 className="dossier__heading">
                                Wat er moet gebeuren
                            </h4>
                            <ul className="dossier__actions">
                                {openActions.map((action) => (
                                    <ActionRow
                                        key={action.code}
                                        action={action}
                                        nodesByKey={nodesByKey}
                                        onFocus={setFocus}
                                    />
                                ))}
                            </ul>
                        </section>
                    )}

                    {doneActions.length > 0 && (
                        <details className="dossier__done">
                            <summary>
                                Al afgehandeld (
                                {doneActions.reduce(
                                    (n, a) => n + a.nodeKeys.length,
                                    0
                                )}
                                )
                            </summary>
                            <ul className="dossier__actions">
                                {doneActions.map((action) => (
                                    <ActionRow
                                        key={action.code}
                                        action={action}
                                        nodesByKey={nodesByKey}
                                        onFocus={setFocus}
                                    />
                                ))}
                            </ul>
                        </details>
                    )}

                    {(dossier.settlements?.length ?? 0) > 0 ? (
                        <SettlementView dossier={dossier} />
                    ) : (
                        <section className="dossier__block" aria-label="Verloop">
                            <h4 className="dossier__heading">Verloop</h4>
                            <ObjectFlow dossier={dossier} focusKey={dossier.focusKey} />
                        </section>
                    )}

                    <div className="dossier__columns">
                        <section
                            className="dossier__block"
                            aria-label="Objecten"
                        >
                            <h4 className="dossier__heading">Objecten</h4>
                            <DossierTree
                                nodes={dossier.nodes}
                                parentKey={null}
                                focus={focus}
                                searched={dossier.focusKey ?? null}
                                onFocus={(key) =>
                                    setFocus(focus === key ? null : key)
                                }
                                onTrace={onTrace}
                            />
                        </section>

                        <section
                            className="dossier__block"
                            aria-label="Tijdlijn"
                        >
                            <div className="dossier__timeline-head">
                                <h4 className="dossier__heading">
                                    Tijdlijn
                                    {focus && (
                                        <span className="dossier__focus">
                                            {nodeLabel(nodesByKey.get(focus))}
                                            <button
                                                type="button"
                                                className="dossier__link"
                                                onClick={() => setFocus(null)}
                                            >
                                                alles
                                            </button>
                                        </span>
                                    )}
                                </h4>

                                <label className="dossier-search__check">
                                    <input
                                        type="checkbox"
                                        checked={onlyNoteworthy}
                                        onChange={(e) =>
                                            setOnlyNoteworthy(e.target.checked)
                                        }
                                    />
                                    Meldingen verbergen
                                </label>
                            </div>

                            {days.map((day) => (
                                <div key={day.day} className="dossier__day">
                                    <h5 className="dossier__day-label">
                                        {day.label}
                                    </h5>
                                    <ol className="dossier__moments">
                                        {day.moments.map((moment) => (
                                            <MomentRow
                                                key={moment.id}
                                                moment={moment}
                                                node={nodesByKey.get(
                                                    moment.nodeKey
                                                )}
                                                showNode={!focus}
                                                onTrace={onTrace}
                                            />
                                        ))}
                                    </ol>
                                </div>
                            ))}

                            {moments.length === 0 && (
                                <p className="dossier__muted">
                                    Niets te tonen
                                    {onlyNoteworthy
                                        ? ' zonder meldingen'
                                        : ''}{' '}
                                    tussen {dossier.from.substring(0, 10)} en{' '}
                                    {dossier.to.substring(0, 10)}.
                                </p>
                            )}
                        </section>
                    </div>

                    {dossier.gaps.length > 0 && (
                        <details className="dossier__gaps">
                            <summary>
                                Wat dit dossier niet kon zien (
                                {dossier.gaps.length})
                            </summary>
                            <ul>
                                {dossier.gaps.map((gap) => (
                                    <li key={gap}>{gap}</li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}
        </CollapsibleSection>
    );
};

/** Grey blocks where the dossier will be, so an old one is never mistaken for the new. */
const DossierSkeleton = ({
    query,
    onCancel,
}: {
    query?: string;
    onCancel: () => void;
}) => (
    <div className="dossier dossier--loading" role="status">
        <p className="dossier__muted">
            <FontAwesomeIcon icon="spinner" spin /> Dossier van {query}{' '}
            opbouwen. Het algemene logboek wordt per dag doorgelezen; dat duurt
            meestal 10 tot 30 seconden.{' '}
            <button type="button" className="dossier__link" onClick={onCancel}>
                Annuleren
            </button>
        </p>
        <div className="dossier__skeleton dossier__skeleton--verdict" />
        <div className="dossier__columns">
            <div>
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="dossier__skeleton dossier__skeleton--row"
                    />
                ))}
            </div>
            <div>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                        key={i}
                        className="dossier__skeleton dossier__skeleton--row"
                    />
                ))}
            </div>
        </div>
    </div>
);

interface IActionRowProps {
    action: DossierAction;
    nodesByKey: Map<string, DossierNode>;
    onFocus: (key: string) => void;
}

const ActionRow = ({ action, nodesByKey, onFocus }: IActionRowProps) => {
    const [open, setOpen] = useState<boolean>(false);

    return (
        <li className="dossier__action">
            <OwnerBadge owner={action.owner} />

            <div className="dossier__action-body">
                <span className="dossier__action-title">{action.title}</span>
                {action.action && (
                    <span className="dossier__action-do">{action.action}</span>
                )}

                {action.details.length > 0 && (
                    <ul className="interpretation__details">
                        {action.details.map((detail) => (
                            <li key={detail}>{detail}</li>
                        ))}
                    </ul>
                )}

                <div className="dossier__action-meta">
                    {action.nodeKeys.map((key) => (
                        <button
                            key={key}
                            type="button"
                            className="dossier__object-chip"
                            onClick={() => onFocus(key)}
                            title="Toon alleen dit object in de tijdlijn"
                        >
                            {nodeLabel(nodesByKey.get(key))}
                        </button>
                    ))}

                    {action.steps.length > 0 && (
                        <button
                            type="button"
                            className="dossier__link"
                            onClick={() => setOpen(!open)}
                            aria-expanded={open}
                        >
                            {open ? 'Stappen verbergen' : 'Stappen tonen'}
                        </button>
                    )}
                </div>

                {open && (
                    <ol className="interpretation__steps">
                        {action.steps.map((step) => (
                            <li key={step}>{step}</li>
                        ))}
                    </ol>
                )}
            </div>
        </li>
    );
};

/**
 * The facts of a node: what the document says, and what the connector books it on in Exact.
 * Closed by default, because a dossier of a dozen lines would otherwise be all details; what
 * points at a likely cause is shown either way.
 */
const DetailList = ({ details }: { details: DossierDetail[] }) => {
    const [open, setOpen] = useState<boolean>(false);
    const warnings = details.filter((d) => d.warning);
    const shown = open ? details : warnings;

    return (
        <div className="dossier__details">
            {shown.length > 0 && (
                <dl className="dossier__facts">
                    {shown.map((detail) => (
                        <div
                            key={`${detail.label}|${detail.value}`}
                            className={
                                detail.warning
                                    ? 'dossier__fact dossier__fact--warning'
                                    : 'dossier__fact'
                            }
                        >
                            <dt>{detail.label}</dt>
                            <dd>
                                {detail.warning && (
                                    <FontAwesomeIcon icon="triangle-exclamation" />
                                )}{' '}
                                <span className="dossier__fact-value">
                                    {detail.value}
                                </span>
                                {detail.source && (
                                    <span className="dossier__fact-source">
                                        {detail.derived ? 'afgeleid: ' : ''}
                                        {detail.source}
                                    </span>
                                )}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
            <button
                type="button"
                className="dossier__link"
                onClick={() => setOpen(!open)}
            >
                {open ? 'Minder' : `Details (${details.length})`}
            </button>
        </div>
    );
};

interface IDossierTreeProps {
    nodes: DossierNode[];
    parentKey: string | null;
    focus: string | null;
    searched: string | null;
    onFocus: (key: string) => void;
    onTrace: (id: string, objectType: string, relationId: string) => void;
}

const DossierTree = ({
    nodes,
    parentKey,
    focus,
    searched,
    onFocus,
    onTrace,
}: IDossierTreeProps) => {
    const children = nodes.filter(
        (node) => (node.parentKey ?? null) === parentKey
    );

    if (children.length === 0) {
        return null;
    }

    return (
        <ul
            className={
                parentKey
                    ? 'dossier__tree dossier__tree--nested'
                    : 'dossier__tree'
            }
        >
            {children.map((node) => {
                const state =
                    STATE[node.exactState] ?? STATE[ErrorRelationState.Unknown];

                // A line or a subscription describes a document; it has no Exact state of its
                // own and no timeline to narrow down to.
                const descriptive = DESCRIPTIVE_ROLES.includes(node.role);

                return (
                    <li key={node.key}>
                        <div
                            className={[
                                'dossier__node',
                                focus === node.key
                                    ? 'dossier__node--focus'
                                    : '',
                                searched === node.key
                                    ? 'dossier__node--searched'
                                    : '',
                            ].join(' ')}
                        >
                            <button
                                type="button"
                                className="dossier__node-main"
                                onClick={() =>
                                    descriptive ? undefined : onFocus(node.key)
                                }
                                title={
                                    descriptive
                                        ? node.linkedBy ?? undefined
                                        : focus === node.key
                                        ? 'Hele tijdlijn tonen'
                                        : 'Alleen dit object in de tijdlijn'
                                }
                            >
                                <span className="dossier__role">
                                    {ROLE_LABEL[node.role]}
                                </span>
                                <span className="dossier__id">
                                    {node.relationId}
                                </span>
                                {!descriptive && (
                                    <span
                                        className={`dossier__state dossier__state--${state.tone}`}
                                        title={state.hint}
                                    >
                                        {state.label}
                                    </span>
                                )}
                            </button>

                            <div className="dossier__node-sub">
                                {node.entryNumber && (
                                    <span>boekstuk {node.entryNumber}</span>
                                )}
                                {node.failedAttempts > 0 && (
                                    <span>{node.failedAttempts}× mislukt</span>
                                )}
                                {!node.linkCertain && (
                                    <span
                                        className="dossier__likely"
                                        title={node.linkedBy ?? undefined}
                                    >
                                        waarschijnlijk
                                    </span>
                                )}
                                {node.chargebeeNote && (
                                    <span>Chargebee: {node.chargebeeNote}</span>
                                )}
                                {!descriptive && (
                                    <button
                                        type="button"
                                        className="dossier__link"
                                        onClick={() =>
                                            onTrace(
                                                node.relationId,
                                                node.objectType,
                                                node.relationId
                                            )
                                        }
                                    >
                                        Volgen
                                    </button>
                                )}
                            </div>

                            {(node.details?.length ?? 0) > 0 && (
                                <DetailList details={node.details!} />
                            )}
                        </div>

                        <DossierTree
                            nodes={nodes}
                            parentKey={node.key}
                            focus={focus}
                            searched={searched}
                            onFocus={onFocus}
                            onTrace={onTrace}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

interface IMomentRowProps {
    moment: DossierMoment;
    node?: DossierNode;
    showNode: boolean;
    onTrace: (id: string, objectType: string, relationId: string) => void;
}

const MomentRow = ({ moment, node, showNode, onTrace }: IMomentRowProps) => {
    const [open, setOpen] = useState<boolean>(false);
    const kind = MOMENT[moment.kind];

    return (
        <li
            className={`dossier__moment dossier__moment--${moment.kind.toLowerCase()}`}
        >
            <button
                type="button"
                className="dossier__moment-head"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
            >
                <span className="dossier__moment-time">{when(moment)}</span>
                <FontAwesomeIcon
                    icon={kind.icon}
                    className="dossier__moment-icon"
                    title={kind.label}
                />
                <span className="dossier__moment-main">
                    <span className="dossier__moment-headline">
                        {moment.headline}
                    </span>
                    <span className="dossier__moment-sub">
                        {showNode && node && (
                            <span className="dossier__moment-node">
                                {nodeLabel(node)}
                            </span>
                        )}
                        {moment.detail && <span>{moment.detail}</span>}
                    </span>
                </span>
                {moment.count > 1 && (
                    <span className="dossier__moment-count">
                        {moment.count}×
                    </span>
                )}
            </button>

            {open && (
                <div className="dossier__moment-detail">
                    {moment.interpretation && (
                        <InterpretationNote
                            interpretation={moment.interpretation}
                            bodyOnly
                        />
                    )}

                    {moment.count > 1 && (
                        <p className="dossier__muted">
                            {moment.count} keer:{' '}
                            {moment.occurrences
                                .map((at) => stamp(at))
                                .join(', ')}
                        </p>
                    )}

                    {moment.raw && !moment.derived && (
                        <div>
                            <span className="dossier__label">
                                Melding zoals de connector hem schreef
                            </span>
                            <pre className="dossier__raw">{moment.raw}</pre>
                        </div>
                    )}

                    {moment.derived && (
                        <p className="dossier__muted">
                            Afgeleid: geen logregel legt dit vast, de
                            koppeltabel en de synclog wel.
                        </p>
                    )}

                    {moment.errorLocation && (
                        <p className="dossier__muted">
                            Plek in de code: {moment.errorLocation}
                        </p>
                    )}

                    {node && (
                        <button
                            type="button"
                            className="dossier__link"
                            onClick={() =>
                                onTrace(
                                    node.relationId,
                                    node.objectType,
                                    node.relationId
                                )
                            }
                        >
                            Volledige trace van {nodeLabel(node)}
                        </button>
                    )}
                </div>
            )}
        </li>
    );
};
